import React, { useEffect, useState } from 'react';
import UploadForm from './UploadForm';
import GridPreview from './GridPreview';
import SummaryDialog from './SummaryDialog';
import UploadSummary from './UploadSummary';
import { Button, message as messageApi, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { convertDropdownLabelsToValues, generateSampleExcel, validateExcelRows } from '../../utils/excelHelper';
import { getExcelColumns } from '../../utils/fieldConfigHelpers';
import { FormFieldConfig } from '../../types/formField.types'; 

interface ExcelUploadManagerProps {
  moduleTitle: string;
  fieldConfig: FormFieldConfig[];
  saveApi: (rows: any[]) => Promise<any>;
} 
const ExcelUploadManager = ({ moduleTitle, fieldConfig, saveApi }: ExcelUploadManagerProps) => {
  const [message, contextHolder] = messageApi.useMessage();
  const [rows, setRows] = useState<any[]>([]);
  const [rowErrors, setRowErrors] = useState<any[]>([]);
  const [showDialog, setShowDialog] = useState(false);

  const columnConfig = getExcelColumns(fieldConfig);

  useEffect(() => {
    if (rows.length > 0) {
      const errors = validateExcelRows(rows, fieldConfig);
      setRowErrors(errors);
    } else {
      setRowErrors([]);
    }
  }, [rows, fieldConfig]);

  const validRows = rows.filter((row, index) => {
    const isDeleted = row.excelDeleted;
    const hasErrors = rowErrors?.[index] && Object.keys(rowErrors[index]).length > 0;
    return !isDeleted && !hasErrors;
  });

  const isValidRows = validRows.length > 0;

  const handleSave = async () => {
    const processedRows = validRows.map((row) => {
      const { excelDeleted, ...rest } = row;
      return convertDropdownLabelsToValues(rest, fieldConfig);
    });

    console.log('🟢 Final Rows to Save:', processedRows);

    try {
      const response = await saveApi(processedRows);

      if (response.failedRows && response.failedRows.length > 0) {
        /* const updatedErrors = [...rowErrors];
        response.failedRows.forEach(({ index, errors }) => {
          updatedErrors[index] = errors;
        });
        setRowErrors(updatedErrors); */
        message.error(`${response.failedRows.length} row(s) failed backend validation.`);
      } else {
        message.success(`${processedRows.length} row(s) uploaded successfully.`);
        setRows([]);
        setShowDialog(false);
      }
    } catch (err) {
      console.error('Save failed:', err);
      message.error('An unexpected error occurred during save.');
    }
  };

  const handleCancel = () => {
    setRows([]);
    setShowDialog(false);
    message.info('Upload process canceled');
  };

  return (
    <>
      {contextHolder}
      <div className="h-full flex flex-col">
        <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-2">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {rows.length > 0 ? (
              <UploadSummary
                rowsLength={rows.length}
                rowErrors={rowErrors}
                validRowsLength={validRows.length}
                isValidRows={isValidRows}
                onSave={() => setShowDialog(true)}
                onCancel={handleCancel}
              />
            ) : (
              <>
                <div className="text-base font-semibold text-gray-700">
                  Upload {moduleTitle} Excel File
                </div>
                <div className="flex gap-2">
                  <Tooltip title="Download a sample Excel file to understand the required format">
                    <Button
                      shape="circle"
                      icon={<FontAwesomeIcon icon={faDownload} />}
                      onClick={() => generateSampleExcel(columnConfig, moduleTitle)}
                    />
                  </Tooltip>
                </div>
              </>
            )}
          </div>
        </div>

        {rows.length === 0 && (
          <UploadForm columnConfig={columnConfig} onRowsUpdated={setRows} moduleTitle={moduleTitle} />
        )}

        {rows.length > 0 && (
          <GridPreview
            rowData={rows}
            columnConfig={columnConfig}
            rowErrors={rowErrors}
            setRows={setRows}
          />
        )}

        <SummaryDialog
          visible={showDialog}
          rowCount={validRows.length}
          onConfirm={handleSave}
          onCancel={() => setShowDialog(false)}
        />
      </div>
    </>
  );
};

export default ExcelUploadManager;
